import { Input, Select } from '@/components/ui/FormControls';
import SalesDatePresets from '@/components/sales/SalesDatePresets';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DateRangeFields from '@/components/common/DateRangeFields';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import SalesRecords from '@/components/sales/SalesRecords';
import { useSalesContribution, useContributionOrders } from '@/hooks/useSalesContribution';
import { fetchAllSales, financialSummary, type SalesFilters } from '@/lib/salesOverview';
import type { SaleWithCustomer } from '@/hooks/useSales';
import { saleMoney, salesDateRange } from '@klothiq/domain';
import { exportToExcel } from '@/utils/exportUtils';
import { toast } from 'sonner';

export default function SalesPerformancePage({ team = false }: { team?: boolean }) {
  const { organization, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = salesDateRange('month', organization?.timezone || 'Asia/Kolkata');
  const [from, setFrom] = useState(initial.from),
    [to, setTo] = useState(initial.to),
    [mode, setMode] = useState<'seller' | 'created'>('seller');
  const [linked, setLinked] = useState(false),
    [zero, setZero] = useState(false),
    [status, setStatus] = useState('all'),
    [page, setPage] = useState(0),
    [peoplePage, setPeoplePage] = useState(0),
    [exporting, setExporting] = useState(false);
  const filters: SalesFilters = {
    scope: team ? 'team' : 'own',
    from,
    to,
    mode,
    status,
    include_linked: linked,
    include_zero: zero,
    ...(team && params.get('person') ? { person_key: params.get('person')! } : {}),
  };
  const summary = useSalesContribution({ ...filters, offset: peoplePage * 25, limit: 25 });
  const orders = useContributionOrders({ ...filters, offset: page * 25, limit: 25 });
  const invalid = !from || !to || from > to;
  const totals = summary.data?.totals;
  function changeDates(value: string | undefined, start: boolean) {
    start ? setFrom(value ?? '') : setTo(value ?? '');
    setPage(0);
    setPeoplePage(0);
  }
  async function download() {
    if (exporting || invalid) return;
    setExporting(true);
    try {
      const all = await fetchAllSales<SaleWithCustomer>({ ...filters, export: true });
      exportToExcel({
        fileName: `${team ? 'Team' : 'My'}_Sales_${from}_${to}`,
        sheetName: 'Sales',
        filtersSummary: `${from} to ${to} · ${mode === 'seller' ? 'Seller credit' : 'Entered by'} · Invoice values before return adjustment`,
        columns: [
          { header: 'Sale', key: 'sale_number' },
          { header: 'Date', key: 'sale_date', format: 'date' },
          { header: 'Customer', key: 'customer' },
          { header: 'Seller', key: 'seller' },
          { header: 'Entered by', key: 'creator' },
          { header: 'Invoice', key: 'invoice', format: 'currency' },
          { header: 'Collected', key: 'collected', format: 'currency' },
          { header: 'Refunded', key: 'refunded', format: 'currency' },
          { header: 'Net collected', key: 'net', format: 'currency' },
          { header: 'Known due / review', key: 'due' },
          { header: 'Sale status', key: 'sale_status' },
          { header: 'Payment status', key: 'payment_status' },
        ],
        data: all.map((s) => {
          const f = financialSummary(s);
          return {
            sale_number: s.sale_number,
            sale_date: s.sale_date,
            customer: s.customer_name || s.customers?.name || 'Walk-in',
            seller: s.seller_name,
            creator: s.entered_by_name,
            invoice: Number(s.total_amount),
            collected: f.collected,
            refunded: f.refunded,
            net: f.net_collected,
            due: f.return_review
              ? 'Review return'
              : f.amount_due === null
                ? 'Not applicable'
                : f.amount_due,
            sale_status: s.sale_status,
            payment_status: s.payment_status,
          };
        }),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }
  return (
    <div className="space-y-5">
      <PageHeader
        title={team ? 'Team Sales' : 'My Sales'}
        description={
          team
            ? 'Sales credited to each seller, with order and payment detail.'
            : 'Your sales activity, seller credit and order details.'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {team ? (
              <Link to="/my-sales" className="rounded-lg border border-border px-4 py-2 text-sm">
                My Sales
              </Link>
            ) : (
              can('sales_contribution.view_all') && (
                <Link
                  to="/team-sales"
                  className="rounded-lg border border-border px-4 py-2 text-sm"
                >
                  Team Sales
                </Link>
              )
            )}
            {can('sales_contribution.export') && (
              <button
                disabled={exporting || invalid}
                onClick={() => void download()}
                className="min-h-11 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {exporting ? 'Preparing…' : 'Export all matching'}
              </button>
            )}
          </div>
        }
      />
      <SalesDatePresets
        onChange={(r) => {
          setFrom(r.from);
          setTo(r.to);
          setPage(0);
          setPeoplePage(0);
        }}
      />
      <section className="space-y-4 rounded-xl border border-border bg-card p-4">
        <div className="grid gap-3 md:grid-cols-2">
          <DateRangeFields
            from={from}
            to={to}
            onFromChange={(v) => changeDates(v, true)}
            onToChange={(v) => changeDates(v, false)}
          />
          <label className="text-xs font-medium text-muted-foreground">
            Track by
            <Select
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as typeof mode);
                setPage(0);
                setPeoplePage(0);
                setParams({});
              }}
              helpLabel="Track by"
            >
              <option value="seller">Seller credit</option>
              <option value="created">Entered by</option>
            </Select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label>
            Status{' '}
            <Select
              className="ml-2 min-h-11 rounded-lg border border-border bg-background px-3"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
                setPeoplePage(0);
              }}
              helpLabel="Status"
            >
              {[
                'all',
                'draft',
                'booked',
                'confirmed',
                'delivery_pending',
                'delivered',
                'partially_returned',
                'returned',
                'cancelled',
              ].map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </Select>
          </label>
          {!team && mode === 'seller' && (
            <label className="flex items-center gap-2">
              <Input
                type="checkbox"
                checked={linked}
                onChange={(e) => {
                  setLinked(e.target.checked);
                  setPage(0);
                }}
                helpLabel="Include my linked staff history"
              />
              Include my linked staff history
            </label>
          )}
          {team && (
            <label className="flex items-center gap-2">
              <Input
                type="checkbox"
                checked={zero}
                onChange={(e) => {
                  setZero(e.target.checked);
                  setPeoplePage(0);
                }}
                helpLabel="Show people with no sales"
              />
              Show people with no sales
            </label>
          )}
          {params.get('person') && (
            <button
              className="text-primary"
              onClick={() => {
                setParams({});
                setPage(0);
                setPeoplePage(0);
              }}
            >
              Clear person filter
            </button>
          )}
        </div>
      </section>
      {invalid ? (
        <ErrorState message="Choose a start and end date in order." />
      ) : summary.isLoading ? (
        <LoadingState />
      ) : summary.error ? (
        <ErrorState message={summary.error.message} onRetry={() => void summary.refetch()} />
      ) : (
        totals && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                ['Eligible orders', String(totals.order_count)],
                ['Collections', saleMoney(totals.collected)],
                ['Refunds', saleMoney(totals.refunded)],
                [
                  'Average invoice value',
                  totals.order_count ? saleMoney(totals.invoice_value / totals.order_count) : '—',
                ],
                ['Returned units', String(totals.returned_units)],
                ['Invoice value', saleMoney(totals.invoice_value)],
                ['Net collected', saleMoney(totals.net_collected)],
                ['Known due', saleMoney(totals.amount_due)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-2 text-lg font-bold">{value}</p>
                </div>
              ))}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Invoice value excludes drafts, cancelled and fully returned orders; partial returns
              retain the original invoice value. Collections and refunds are lifetime ledger values
              for sales dated in this range. Seller credit and entry activity are separate views.
              Drafts: {totals.draft_count} · Cancelled: {totals.cancelled_count} · Delivered:{' '}
              {totals.delivered_count} · Returned: {totals.returned_count}.{' '}
              {totals.return_review_count > 0 &&
                `${totals.return_review_count} returned order(s) require balance review and are excluded from known due.`}{' '}
              Updated {new Date(summary.data!.as_of).toLocaleString('en-IN')} ·{' '}
              {summary.data!.timezone}.
            </p>
            <section className="space-y-3">
              <h2 className="font-semibold">{team ? 'People' : 'Credit sources'}</h2>
              {summary.data!.people.length === 0 ? (
                <p className="rounded-xl border border-border p-6 text-sm text-muted-foreground">
                  No sales in this period.
                </p>
              ) : (
                summary.data!.people.map((person) => (
                  <button
                    key={person.person_key}
                    type="button"
                    disabled={!team}
                    onClick={() => {
                      setParams({ person: person.person_key });
                      setPage(0);
                      setPeoplePage(0);
                    }}
                    className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 text-left enabled:hover:border-primary/50"
                  >
                    <div>
                      <p className="font-semibold">
                        {person.name}{' '}
                        {!person.active && (
                          <span className="text-xs font-normal text-muted-foreground">
                            Inactive / historical
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {person.person_key.startsWith('staff:') ? 'Staff seller' : 'Login user'} ·{' '}
                        {person.order_count} orders · {person.delivered_count} delivered ·{' '}
                        {person.returned_count} returned
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{saleMoney(person.invoice_value)}</p>
                      <p className="text-xs text-muted-foreground">
                        {person.share_percent !== null
                          ? `${person.share_percent}% of selected invoice value · `
                          : ''}
                        Net collected {saleMoney(person.net_collected)}
                      </p>
                    </div>
                  </button>
                ))
              )}
              <div className="flex items-center justify-end gap-3 text-sm">
                <button
                  disabled={peoplePage === 0}
                  onClick={() => setPeoplePage((p) => p - 1)}
                  className="min-h-11 rounded-lg border px-3 disabled:opacity-40"
                >
                  Previous people
                </button>
                <span>{peoplePage + 1}</span>
                <button
                  disabled={(peoplePage + 1) * 25 >= summary.data!.total_people}
                  onClick={() => setPeoplePage((p) => p + 1)}
                  className="min-h-11 rounded-lg border px-3 disabled:opacity-40"
                >
                  Next people
                </button>
              </div>
            </section>
          </>
        )
      )}
      {!invalid && (
        <section className="space-y-3">
          <h2 className="font-semibold">Matching orders</h2>
          {orders.isLoading ? (
            <LoadingState />
          ) : orders.error ? (
            <ErrorState message={orders.error.message} onRetry={() => void orders.refetch()} />
          ) : !orders.data?.rows.length ? (
            <p className="rounded-xl border border-border p-6 text-sm text-muted-foreground">
              No matching orders.
            </p>
          ) : (
            <SalesRecords rows={orders.data.rows} showCreator />
          )}
          <div className="flex items-center justify-end gap-3 text-sm">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="min-h-11 rounded-lg border px-3 disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              {orders.data?.total ?? 0} orders · Page {page + 1}
            </span>
            <button
              disabled={(page + 1) * 25 >= (orders.data?.total ?? 0)}
              onClick={() => setPage((p) => p + 1)}
              className="min-h-11 rounded-lg border px-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
